import * as THREE from 'three';
import type { SeededRandom } from '../utils/random';
import type { Mineral } from './minerals';
import { spawnMineral } from './minerals';
import { WORLD } from './config';

export type WorldEventType = 'goldRain' | 'caveIn' | 'blackout' | 'veinShift' | 'gopher' | 'none';

export interface WorldEvent {
  type: WorldEventType;
  timeLeft: number;
  intensity: number;
}

export class WorldEventManager {
  current: WorldEvent = { type: 'none', timeLeft: 0, intensity: 0 };
  private nextIn = 18;
  private elapsed=0;
  private pivots:readonly number[]=[0];
  private options: readonly WorldEventType[] = ['goldRain', 'caveIn', 'blackout', 'veinShift', 'gopher'];
  private readonly particles: THREE.Points;
  private readonly particlePositions: Float32Array;
  private readonly particleVel: Float32Array;
  private blackoutFactor = 0;

  constructor(
    private readonly scene: THREE.Scene,
    private rng: SeededRandom,
    private readonly onEvent: (type: WorldEventType) => void,
  ) {
    const count = 400;
    this.particlePositions = new Float32Array(count * 3);
    this.particleVel = new Float32Array(count * 3);
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(this.particlePositions, 3));
    const mat = new THREE.PointsMaterial({
      color: '#FFE08A',
      size: 0.12,
      transparent: true,
      opacity: 0,
      depthWrite: false,
    });
    this.particles = new THREE.Points(geo, mat);
    this.scene.add(this.particles);
  }

  lightUp(): void { this.blackoutFactor = 0; }

  get isBlackout(): boolean {
    return this.current.type === 'blackout' && this.current.timeLeft > 0;
  }

  get blackoutAmount(): number {
    return this.blackoutFactor;
  }

  reset(rng: SeededRandom, options?: readonly WorldEventType[], pivots:readonly number[]=[0]): void {
    this.pivots=pivots;
    if (options) this.options = options;
    this.rng = rng;
    this.current = { type: 'none', timeLeft: 0, intensity: 0 };
    this.blackoutFactor = 0;this.elapsed=0;
    (this.particles.material as THREE.PointsMaterial).opacity = 0;
  }

  schedule(early: boolean): void {
    this.nextIn = early ? 10 + this.rng() * 8 : 16 + this.rng() * 14;
  }

  update(dt: number, minerals: Mineral[], playerHooks: { x: number; y: number }[]): void {
    this.elapsed+=dt;
    if (this.current.timeLeft > 0) {
      this.current.timeLeft -= dt;
      this.updateActive(dt, minerals, playerHooks);
      if (this.current.timeLeft <= 0) {
        this.endEvent(minerals);
      }
    } else {
      this.nextIn -= dt;
      if (this.nextIn <= 0) {
        this.startRandom();
      }
      this.blackoutFactor = Math.max(0, this.blackoutFactor - dt * 1.5);
    }
  }

  private startRandom(): void {
    const type = this.options[Math.floor(this.rng() * this.options.length)];
    const duration =
      type === 'blackout' ? 5 : type === 'goldRain' ? 8 : type === 'caveIn' ? 6 : type === 'gopher' ? 7 : 5;
    this.current = { type, timeLeft: duration, intensity: 1 };
    this.onEvent(type);
    if (type === 'goldRain') this.spawnGoldRain([], this.rng);
    this.schedule(false);
  }

  bindMinerals(_m: Mineral[]): void {
    // minerals are passed each update; kept for API stability
  }

  private spawnGoldRain(_unused: Mineral[], rng: SeededRandom): void {
    const count = 80;
    const mat = this.particles.material as THREE.PointsMaterial;
    mat.opacity = 0.95;
    mat.color.set('#FFE08A');
    for (let i = 0; i < count; i++) {
      const i3 = i * 3;
      this.particlePositions[i3] = (rng() * 2 - 1) * WORLD.halfWidth;
      this.particlePositions[i3 + 1] = WORLD.topY + rng() * 4;
      this.particlePositions[i3 + 2] = rng() * 1.5;
      this.particleVel[i3] = (rng() - 0.5) * 0.4;
      this.particleVel[i3 + 1] = -3.5 - rng() * 2.5;
      this.particleVel[i3 + 2] = 0;
    }
    this.particles.geometry.attributes.position.needsUpdate = true;
  }

  private updateActive(dt: number, minerals: Mineral[], _hooks: { x: number; y: number }[]): void {
    switch (this.current.type) {
      case 'goldRain': {
        const mat = this.particles.material as THREE.PointsMaterial;
        mat.opacity = 0.9;
        for (let i = 0; i < 80; i++) {
          const i3 = i * 3;
          this.particlePositions[i3] += this.particleVel[i3] * dt;
          this.particlePositions[i3 + 1] += this.particleVel[i3 + 1] * dt;
          if (this.particlePositions[i3 + 1] < WORLD.floorY) {
            this.particlePositions[i3 + 1] = WORLD.topY + 2;
            this.particlePositions[i3] = (this.rng() * 2 - 1) * WORLD.halfWidth;
          }
        }
        this.particles.geometry.attributes.position.needsUpdate = true;
        break;
      }
      case 'caveIn': {
        if (this.rng() < dt * 1.8 && minerals.length < 48) {
          const x = (this.rng() * 2 - 1) * (WORLD.halfWidth - 1.2);
          minerals.push(spawnMineral(8000 + minerals.length, 'rock', x, WORLD.topY - 0.5, this.scene));
          const rock = minerals[minerals.length - 1];
          // velocity applied via bobPhase abuse - store fall speed in bobPhase
          rock.bobPhase = -20;
        }
        // fall rocks
        for (const m of minerals) {
          if (m.active && m.grabbedBy === null && m.kind === 'rock' && m.bobPhase <= -1) {
            m.y += m.bobPhase * dt * 0.15;
            if (m.y < WORLD.floorY + 1) {
              m.y = WORLD.floorY + 1;
              m.bobPhase = 0;
            }
            m.group.position.set(m.x, m.y, 0);
          }
        }
        break;
      }
      case 'blackout': {
        this.blackoutFactor = Math.min(1, this.blackoutFactor + dt * 2);
        break;
      }
      case 'veinShift': {
        for (const m of minerals) {
          if (!m.active || m.grabbedBy !== null) continue;
          const nextX=Math.max(-WORLD.halfWidth+1,Math.min(WORLD.halfWidth-1,m.x+Math.sin(m.bobPhase+this.elapsed)*dt*.35));
          if(!this.pivots.some(p=>Math.abs(Math.atan2(nextX-p,WORLD.pivotY-m.y))<WORLD.swingAmplitude-.02&&Math.hypot(nextX-p,WORLD.pivotY-m.y)<WORLD.maxHookLength-.1))continue;
          if(minerals.some(other=>other!==m&&other.active&&other.grabbedBy===null&&Math.hypot(other.x-nextX,other.y-m.y)<other.radius+m.radius+.15))continue;
          m.x=nextX;
          m.group.position.set(m.x, m.y, 0);
        }
        break;
      }
      default:
        break;
    }
  }

  private endEvent(minerals: Mineral[]): void {
    if (this.current.type === 'goldRain') {
      const mat = this.particles.material as THREE.PointsMaterial;
      mat.opacity = 0;
    }
    if (this.current.type === 'caveIn') {
      for (const m of minerals) {
        if (m.bobPhase <= -1) m.bobPhase = m.id * 0.7;
      }
    }
    this.current = { type: 'none', timeLeft: 0, intensity: 0 };
  }

  dispose(): void {
    this.scene.remove(this.particles);
    this.particles.geometry.dispose();
    (this.particles.material as THREE.Material).dispose();
  }
}

